import { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import type { Booking, Event } from '../types';
import { ArrowLeft } from 'lucide-react';

const BookingConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingDetails, setBookingDetails] = useState<any>(null);

  useEffect(() => {
    // Debug logging for location state
    
    // Check if booking details are passed via location state
    if (location.state?.bookingDetails) {
      setBookingDetails(location.state.bookingDetails);
      setIsLoading(false);
      return;
    }
    
    // If no state is passed, try to fetch booking details using the ID parameter
    const fetchBookingDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!id) {
          // Just set loading to false without error if accessed directly
          // This allows rendering an empty state rather than error
          setIsLoading(false);
          return;
        }

        // Fetch booking details
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select('*')
          .eq('id', id)
          .single();

        if (bookingError) {
          console.error('Error fetching booking:', bookingError);
          setError('Booking not found');
          return;
        }

        if (!bookingData) {
          setError('Booking not found');
          return;
        }

        setBooking(bookingData);

        // Fetch associated event details
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', bookingData.event_id)
          .single();

        if (eventError) {
          console.error('Error fetching event:', eventError);
        } else {
          setEvent(eventData);
        }
      } catch (err) {
        console.error('Error fetching booking details:', err);
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id, location.state]);

  // Format date from ISO string
  const formatISODate = (isoString: string) => {
    try {
      const date = parseISO(isoString);
      // Convert UTC to local time
      const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return format(localDate, 'EEEE, MMMM d, yyyy');
    } catch (err) {
      console.error('Error formatting ISO date:', err);
      return 'Date not available';
    }
  };

  // Format time from ISO string
  const formatISOTime = (isoString: string) => {
    try {
      const date = parseISO(isoString);
      // Convert UTC to local time
      const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return format(localDate, 'h:mm a');
    } catch (err) {
      console.error('Error formatting ISO time:', err);
      return 'Time not available';
    }
  };

  const formatDateTime = (date: string, time: string) => {
    try {
      const dateTimeString = `${date}T${time}:00`;
      const parsedDate = parseISO(dateTimeString);
      // Convert UTC to local time
      const localDate = new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);
      return format(localDate, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
    } catch (err) {
      console.error('Error formatting date/time:', err);
      return `${date} at ${time}`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If we have state-based booking details, render those
  if (bookingDetails) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-success/5 p-6 border-b border-green-100">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-success/10 rounded-full p-2">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-8 w-8 text-success" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-center text-foreground mb-2">Booking Confirmed!</h1>
            <p className="text-center text-gray-600">
              Your site survey has been scheduled successfully.
            </p>
          </div>

          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Appointment Details</h2>
            
            <div className="space-y-4">
              <div className="bg-accent/5 p-4 rounded-md">
                <h3 className="font-medium text-accent mb-2">Date & Time</h3>
                <p className="text-foreground">
                  {bookingDetails.startTime ? (
                    <>
                      {(() => {
                        const date = parseISO(bookingDetails.startTime);
                        // Convert UTC to local time
                        const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                        return format(localDate, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
                      })()}
                    </>
                  ) : (
                    'To be scheduled'
                  )}
                </p>
                {bookingDetails.message && (
                  <p className="text-muted-foreground text-sm mt-1">{bookingDetails.message}</p>
                )}
              </div>

              {bookingDetails.address && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="font-medium text-foreground mb-2">Location</h3>
                  <p className="text-foreground">{bookingDetails.address}</p>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-medium text-foreground mb-2">Contact Information</h3>
                <p className="text-foreground">
                  {bookingDetails.firstName} {bookingDetails.lastName}
                </p>
                <p className="text-foreground">{bookingDetails.email}</p>
                <p className="text-foreground">{bookingDetails.phoneNumber}</p>
                {bookingDetails.salesRepName && (
                  <p className="text-foreground">Sales Rep: {bookingDetails.salesRepName}</p>
                )}
              </div>
              
              {bookingDetails.extraInformation && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="font-medium text-foreground mb-2">Additional Details</h3>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
                    {bookingDetails.extraInformation.levels && (
                      <p><span className="font-medium">Levels:</span> {bookingDetails.extraInformation.levels}</p>
                    )}
                    {bookingDetails.extraInformation.atticAccess && (
                      <p><span className="font-medium">Attic Access:</span> {bookingDetails.extraInformation.atticAccess}</p>
                    )}
                    {bookingDetails.extraInformation.mountType && (
                      <p><span className="font-medium">Mount Type:</span> {bookingDetails.extraInformation.mountType}</p>
                    )}
                    {bookingDetails.extraInformation.metersOnSite && (
                      <p><span className="font-medium">Meters On Site:</span> {bookingDetails.extraInformation.metersOnSite}</p>
                    )}
                    {bookingDetails.extraInformation.electricalPanels && (
                      <p><span className="font-medium">Electrical Panels:</span> {bookingDetails.extraInformation.electricalPanels}</p>
                    )}
                    {bookingDetails.extraInformation.animals && (
                      <p><span className="font-medium">Animals:</span> {bookingDetails.extraInformation.animals}</p>
                    )}
                    {bookingDetails.extraInformation.hasBattery && (
                      <p><span className="font-medium">Has Battery:</span> {bookingDetails.extraInformation.hasBattery}</p>
                    )}
                    {bookingDetails.extraInformation.requiresConstruction && (
                      <p><span className="font-medium">Requires Construction:</span> {bookingDetails.extraInformation.requiresConstruction}</p>
                    )}
                    {bookingDetails.extraInformation.constructionDetails && (
                      <p className="col-span-2"><span className="font-medium">Construction Details:</span> {bookingDetails.extraInformation.constructionDetails}</p>
                    )}
                    {bookingDetails.extraInformation.installerNote && (
                      <p className="col-span-2"><span className="font-medium">Installer Note:</span> {bookingDetails.extraInformation.installerNote}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 border-t border-border pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                {bookingDetails.emailSent ? (
                  <>
                    {bookingDetails.salesRepEmail ? (
                      <>
                        ✅ A confirmation email has been sent to your assigned sales representative ({bookingDetails.salesRepEmail}) with these details.
                      </>
                    ) : (
                      <>
                        ✅ A confirmation email has been sent to {bookingDetails.confirmationEmailSentTo || bookingDetails.email} with these details.
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {bookingDetails.salesRepEmail ? (
                      <>
                        ⚠️ Your booking has been confirmed, but the confirmation email to your sales representative ({bookingDetails.salesRepEmail}) could not be sent at this time.
                        {bookingDetails.emailError && (
                          <span className="block text-xs text-red-600 mt-1">
                            Error: {bookingDetails.emailError}
                          </span>
                        )}
                        <span className="block mt-1">Please contact them directly to confirm your appointment.</span>
                      </>
                    ) : (
                      <>
                        ⚠️ Your booking has been confirmed, but a confirmation email could not be sent at this time.
                        {bookingDetails.emailError && (
                          <span className="block text-xs text-red-600 mt-1">
                            Error: {bookingDetails.emailError}
                          </span>
                        )}
                      </>
                    )}
                  </>
                )}
              </p>

              <div className="flex justify-between">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-border shadow-sm text-sm font-medium rounded-md text-foreground bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Return Home
                </button>

                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Print Confirmation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Handle empty state - if no data is passed through state and no ID parameter
  if (!bookingDetails && !booking && !error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-border">
            <h1 className="text-2xl font-bold text-center text-foreground mb-2">No Booking Information</h1>
            <p className="text-center text-gray-600">
              No booking information is available to display.
            </p>
          </div>
          
          <div className="p-6 text-center">
            <p className="text-gray-600 mb-6">
              If you're trying to book a site survey, please visit our site survey booking page.
            </p>
            
            <div className="flex justify-center">
              <Link
                to="/app/sitesurvey"
                className="px-4 py-2 text-white rounded-md bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Go to Site Survey
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Handle error case - if there was an error fetching data
  if (error || !booking) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-destructive/5 border border-destructive/20 text-destructive px-4 py-3 rounded-md max-w-md w-full">
          <h2 className="text-lg font-medium">Error</h2>
          <p>{error || 'Booking not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-success/5 p-6 border-b border-green-100">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-success/10 rounded-full p-2">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-8 w-8 text-success" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 13l4 4L19 7" 
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center text-foreground mb-2">Booking Confirmed!</h1>
          <p className="text-center text-gray-600">
            Your site survey has been scheduled successfully.
          </p>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">Appointment Details</h2>
          
          <div className="space-y-4">
            <div className="bg-accent/5 p-4 rounded-md">
              <h3 className="font-medium text-accent mb-2">Date & Time</h3>
              <p className="text-foreground">
                {formatDateTime(booking.booking_date, booking.booking_time)}
              </p>
              {event && (
                <p className="text-muted-foreground text-sm mt-1">
                  Duration: {event.duration_minutes} minutes
                </p>
              )}
            </div>

            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium text-foreground mb-2">Location</h3>
              <p className="text-foreground">{booking.address}</p>
              <p className="text-foreground">
                {booking.city}, {booking.state} {booking.postal_code}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium text-foreground mb-2">Contact Information</h3>
              <p className="text-foreground">
                {booking.first_name} {booking.last_name}
              </p>
              <p className="text-foreground">{booking.email}</p>
              <p className="text-foreground">{booking.phone_number}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium text-foreground mb-2">Booking Reference</h3>
              <p className="text-foreground font-mono">{booking.id}</p>
            </div>
          </div>

          <div className="mt-8 border-t border-border pt-6 text-center">
            <p className="text-gray-600 mb-4">
              You'll receive a confirmation email shortly with these details.
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/"
                className="px-4 py-2 text-white rounded-md bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
